import { ProcedureTemplateCsv, ProcedureTemplateRowCsv } from '../../types/procedure-template-csv';

const NAME_MAX_CHAR = 250;
const DESCRIPTION_MAX_CHAR = 2000;

/**
 * Check if csv file is valid
 * @param rows Data parsed from csv file
 * @returns [{errorMessage}, ProcedureTemplateRow[]]
 */
export function validateData(rows: ProcedureTemplateCsv[]): [{ [rowNumber: string]: string[] }, ProcedureTemplateRowCsv[]] {
    const errorMessages: { [rowNumber: string]: string[] } = {};
    const procedureTemplateRows: ProcedureTemplateRowCsv[] = [];

    const templateNames: string[] = [];
    const workpaperRoleTypes = ['PREPARER', 'REVIEWER', 'APPROVER'];
    const entityTypes = [
        'COMPANY',
        'TRUST',
        'PARTNERSHIP',
        'SOLE_TRADER',
        'INDIVIDUAL',
        'UNIT_TRUST',
        'ESTATE',
        'SELF_MANAGED_SUPERANNUATION_FUND',
        'SUPERANNUATION_FUND',
        'GOVERNMENT_ENTITY',
        'CLUB_OR_SOCIETY',
        'NOT_FOR_PROFIT',
        'OTHER',
        'ACCOUNTING_PRACTICE',
        'CHARITY',
        'LOOK_THROUGH_COMPANY',
        'S_CORPORATION',
    ];

    for (const row of rows) {
        const rowNumber = `${rows.indexOf(row) + 2}`;
        const messages: string[] = [];

        // Skip empty rows
        if (Object.values(row).every((v) => !v)) {
            continue;
        }

        if (!row['Procedure Name *']?.trim()) {
            messages.push(`'Procedure Name' field cannot be empty.`);
        }

        if (row['Procedure Name *']?.trim().length > NAME_MAX_CHAR) {
            messages.push(`'Procedure Name' must be within ${NAME_MAX_CHAR} characters.`);
        }

        if (templateNames.includes(row['Procedure Name *']?.toLocaleLowerCase()?.trim())) {
            messages.push(`Values in 'Procedure Name' field must be unique.`);
        } else {
            templateNames.push(row['Procedure Name *'].toLocaleLowerCase().trim());
        }

        if (!row['Description *']?.trim()) {
            messages.push(`'Description' field cannot be empty.`);
        }

        if (row['Description *'] && row['Description *'].trim().length > DESCRIPTION_MAX_CHAR) {
            messages.push(`'Description' must be within ${DESCRIPTION_MAX_CHAR} characters.`);
        }

        if (!row['Workpaper Role *']?.trim()) {
            messages.push(`'Workpaper Role' field cannot be empty.`);
        }

        if (row['Workpaper Role *'] && !workpaperRoleTypes.includes(row['Workpaper Role *'].toLocaleUpperCase())) {
            messages.push(`Values in 'Workpaper Role' field must be either 'PREPARER', 'REVIEWER', 'APPROVER'.`);
        }

        if (row['Entity Types']) {
            const entitiesArray = row['Entity Types'].split(' ');
            if (!entitiesArray.every((item) => entityTypes.includes(item.toUpperCase()))) {
                messages.push(
                    `Values in 'Entity Types' field must be either 'COMPANY', 'TRUST', 'PARTNERSHIP', 'SOLE_TRADER', 'INDIVIDUAL', 'UNIT_TRUST', 'ESTATE', 'SELF_MANAGED_SUPERANNUATION_FUND', 'SUPERANNUATION_FUND', 'GOVERNMENT_ENTITY', 'CLUB_OR_SOCIETY', 'NOT_FOR_PROFIT', 'OTHER', 'ACCOUNTING_PRACTICE', 'CHARITY', 'LOOK_THROUGH_COMPANY' or 'S_CORPORATION', and must be separated by a single space.`
                );
            }
        }

        if (messages.length !== 0) {
            errorMessages[rowNumber] = messages;
        } else {
            const procedureTemplateRow = {
                name: row['Procedure Name *'].trim(),
                description: row['Description *'].trim(),
                category: row.Category.trim() === '' ? undefined : row.Category.trim(),
                role: row['Workpaper Role *'].trim().toLocaleUpperCase(),
                entityTypes: row['Entity Types']
                    ? row['Entity Types'].split(' ').map((e) => {
                          return e.toUpperCase();
                      })
                    : [],
            };
            procedureTemplateRows.push(procedureTemplateRow);
        }
    }

    return [errorMessages, procedureTemplateRows];
}
